import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Tabs,
  Tab,
  Button,
  Form,
  Table,
  Badge,
  Image,
  Breadcrumb,
  Spinner,
} from 'react-bootstrap';
import { IoCameraOutline } from "react-icons/io5";
import Base from '../Base';
import { useNavigate, useParams, NavLink } from 'react-router-dom';
import { FaCheck, FaUserCircle, FaUser } from 'react-icons/fa';
import DynamicModal from '../Employees/DynamicModal';
import { FaEdit, FaTrash, FaEye, FaPlus } from 'react-icons/fa';
import { toast } from 'react-hot-toast';
import { kycValidationRules, extractDropdownValue } from '../Employees/employeeFormConfig';
import { useSelector, useDispatch } from "react-redux";
import { getActiveUsers, getRoles } from "../../slices/userReducer";
import {
  validateField,
  validateAllFields,
  UPDATE_CLIENT_FIELDS,
  viewClient,
  updateClient,
  listDocument,
  deleteKyc,
  uploadProfilePhoto,
  insertDocument,
  deleteDocument,
  updateClientStatus,
  approveClient,
  listKYCTypes,
  listCategoryTypes
} from './ClientHelper';
import { MdDoubleArrow } from 'react-icons/md';
import instance from "../../services/baseService";
import { truncateText, truncateTextOnly } from '../../utils';
import { extractErrorMessage } from '../../constants/common';
import DynamicCreateModal from '../common/DynamicCreateModal';
import ClientCredentials from './ClientCredentials';
import MonthlyRates from './MonthlyRates';
function ClientProcess() {
  const fileInputRef = useRef();
  const [activeTab, setActiveTab] = useState('general');
  const [showKycForm, setShowKycForm] = useState(false);
  const [editingKycId, setEditingKycId] = useState(null);
  const [kycFormData, setKycFormData] = useState({ document_number: null, document_type: null, photo: null, expires_date: null, notify_day_before: null });
  const [kycErrors, setKycErrors] = useState({});
  const [kycList, setKycList] = useState([]);
  const [saving, setSaving] = useState({ employeeIdentity: false, approveAction: false, rejectAction: false });
  const { id } = useParams();
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    onConfirm: null,
    message: '',
  });
  const [clientData, setClientData] = useState({});
  const [originalClientData, setOriginalClientData] = useState({});
  const [editMode, setEditMode] = useState({ general: false });
  const [loading, setLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  // New state for form validation
  const [validationErrors, setValidationErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [loadingDocumentTypes, setLoadingDocumentTypes] = useState(false);
  // Hierarchical role states
  const [roleOptions, setRoleOptions] = useState({});
  const [loadingRoles, setLoadingRoles] = useState({});
  const [roleFields, setRoleFields] = useState([{
    id: 0,
    value: '',
    selectedRoleId: null,
    error: false
  }]);
  const [nextRoleId, setNextRoleId] = useState(1);
  const [originalRoleFields, setOriginalRoleFields] = useState([{
    id: 0,
    value: '',
    selectedRoleId: null,
    error: false
  }]);

  // Redux setup
  const { activeUsers: userData, roles } = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [currentModalType, setCurrentModalType] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creatingItem, setCreatingItem] = useState(false);
  const [categoryTypes, setCategoryTypes] = useState([]);
  const [loadingCategoryTypes, setLoadingCategoryTypes] = useState(false);
  const kycDropDownCreate = {
    name: 'Document Type',
    placeholder: 'Select Document Type',
    type: 'select',
    label: 'Document Type',
    apiEndpoint: '/api/v1/client/document/type/create', // Adjusted for URL standard
    fields: [
      {
        key: 'name',
        label: 'Document Type',
        required: true,
        maxLength: 80,
        placeholder: 'Enter Document Type'
      }
    ]
  };
  // API call to fetch roles for hierarchical selection
  const fetchRoles = async (roleIndex, parentRoleId = null) => {
    try {
      setLoadingRoles(prev => ({
        ...prev,
        [roleIndex]: true
      }));

      const endpoint = parentRoleId
        ? `/api/v1/rbac/approval/roles/list/${parentRoleId}`
        : `/api/v1/rbac/approval/roles/list`;

      const response = await instance.get(endpoint);

      if (response.data?.type === 'success') {
        setRoleOptions(prev => ({
          ...prev,
          [roleIndex]: response.data.data
        }));
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
      // Fallback to Redux roles if API fails
      setRoleOptions(prev => ({
        ...prev,
        [roleIndex]: roles || []
      }));
    } finally {
      setLoadingRoles(prev => ({
        ...prev,
        [roleIndex]: false
      }));
    }
  };

  const fetchCategoryTypes = async () => {
    setLoadingCategoryTypes(true);
    try {
      const response = await listCategoryTypes();
      setCategoryTypes(response.data);
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      toast.error(errorMessage);
    } finally {
      setLoadingCategoryTypes(false);
    }
  };
  // Load roles for existing data when entering edit mode
  const loadRolesForExistingData = async () => {
    const currentRoles = roleFields;

    // First, fetch roles for the first dropdown
    await fetchRoles(0);

    // Then fetch roles for subsequent dropdowns based on parent selections
    for (let i = 1; i < currentRoles.length; i++) {
      const parentRole = currentRoles[i - 1];
      if (parentRole.selectedRoleId) {
        await fetchRoles(i, parentRole.selectedRoleId);
      }
    }
  };

  const fetchClientData = async () => {
    if (!id) {
      toast.error('Client ID is required');
      navigate('/clients');
      return;
    }

    try {
      setLoading(true);
      const response = await viewClient(id);

      if (response.type === 'success') {
        const clientInfo = response.data;
        Object.keys(UPDATE_CLIENT_FIELDS).forEach(fieldKey => {
          if (UPDATE_CLIENT_FIELDS[fieldKey].type === 'checkbox' &&
            typeof clientInfo[fieldKey] === 'number') {
            clientInfo[fieldKey] = clientInfo[fieldKey] === 1;
          }
        });
        if (clientInfo.categories && !clientInfo.category) {
          clientInfo.category = clientInfo.categories.id;
        }
        setClientData(clientInfo);
        setOriginalClientData(clientInfo);

        // Initialize role fields if approver_levels exists
        if (clientInfo.approver_levels && Array.isArray(clientInfo.approver_levels) && clientInfo.approver_levels.length > 0) {
          const sortedApproverLevels = clientInfo.approver_levels.sort((a, b) => b.level - a.level);

          const initialRoles = sortedApproverLevels.map((level, index) => ({
            id: index,
            value: level.name,
            selectedRoleId: level.id,
            error: false
          }));

          setRoleFields(initialRoles);
          setOriginalRoleFields(initialRoles);
          setNextRoleId(initialRoles.length); // This will be correct for existing data
        } else {
          // For old clients with no approver_levels data
          const defaultRole = [{ id: 0, value: '', selectedRoleId: null, error: false }];
          setRoleFields(defaultRole);
          setOriginalRoleFields(defaultRole);
          setNextRoleId(1); // FIX: Set to 1 instead of 0, since we created a role with id: 0
        }
      }
    } catch (error) {
      console.error('Error fetching client data:', error);
      const errorMessage = extractErrorMessage(error);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  // Fetch client data when component mounts
  useEffect(() => {
    fetchClientData();
    fetchCategoryTypes();
  }, [id, navigate]);


  useEffect(() => {
    dispatch(getActiveUsers({}));
    dispatch(getRoles());
  }, [dispatch]);

  const handleStatusToggle = async (newStatus) => {
    setIsUpdating(true);
    try {
      await updateClientStatus(id, newStatus);
      setClientData(prev => ({
        ...prev,
        is_active: newStatus
      }));
      toast.success(`Client ${newStatus ? 'activated' : 'deactivated'} successfully`);
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      toast.error(errorMessage);
    } finally {
      setIsUpdating(false);
    }
  };

  // Enhanced role management functions for hierarchical selection
  const handleRoleChange = async (e, roleId) => {
    const selectedValue = e.target.value;
    const roleIndex = roleFields.findIndex(role => role.id === roleId);
    const availableRoles = roleOptions[roleId] || [];
    const selectedRole = availableRoles.find(role => role.name === selectedValue);

    // Update role fields
    setRoleFields(prev => prev.map(role =>
      role.id === roleId ? {
        ...role,
        value: selectedValue,
        selectedRoleId: selectedRole ? selectedRole.id : null,
        error: false
      } : role
    ));

    // Clear options for subsequent roles when current selection changes
    const currentRoleIndex = roleFields.findIndex(role => role.id === roleId);

    // Remove role options for roles that come after the current one
    setRoleOptions(prev => {
      const newOptions = { ...prev };
      Object.keys(newOptions).forEach(key => {
        const keyIndex = roleFields.findIndex(role => role.id === parseInt(key));
        if (keyIndex > currentRoleIndex) {
          delete newOptions[key];
        }
      });
      return newOptions;
    });

    // Clear subsequent role selections
    setRoleFields(prev => prev.map((role, index) =>
      index > currentRoleIndex ? {
        ...role,
        value: '',
        selectedRoleId: null,
        error: false
      } : role
    ));

    // ✅ NEW: Auto-fetch options for the immediate next role if it exists
    const nextRoleIndex = currentRoleIndex + 1;
    const nextRole = roleFields[nextRoleIndex];

    if (nextRole && selectedRole) {
      await fetchRoles(nextRole.id, selectedRole.id);
    }

    // Update the actual form data for approver_levels with role IDs
    const updatedRoles = roleFields.map((role, index) => {
      if (role.id === roleId) {
        return {
          ...role,
          value: selectedValue,
          selectedRoleId: selectedRole ? selectedRole.id : null
        };
      } else if (index > currentRoleIndex) {
        return {
          ...role,
          value: '',
          selectedRoleId: null
        };
      }
      return role;
    });

    const selectedRoleIds = updatedRoles
      .filter(role => role.selectedRoleId)
      .map(role => role.selectedRoleId);
    setClientData(prev => ({ ...prev, approver_levels: selectedRoleIds, category: prev.categories?.id || prev.category }));
    if (selectedRole && validationErrors.approver_levels) {
      setValidationErrors(prev => {
        const { approver_levels, ...rest } = prev;
        return rest;
      });
    }
  };
  const handleAddRole = (currentRoleId) => {
    const currentRole = roleFields.find(role => role.id === currentRoleId);

    // Validate current selection
    if (!currentRole.value || !currentRole.selectedRoleId) {
      setRoleFields(prev => prev.map(role =>
        role.id === currentRoleId ? { ...role, error: true } : role
      ));
      return;
    }

    // Clear error and add new role
    const newRoleId = nextRoleId;
    setRoleFields(prev => [
      ...prev.map(role =>
        role.id === currentRoleId ? { ...role, error: false } : role
      ),
      { id: newRoleId, value: '', selectedRoleId: null, error: false }
    ]);

    setNextRoleId(prev => prev + 1);

    // Fetch roles for the new dropdown based on current selection
    fetchRoles(newRoleId, currentRole.selectedRoleId);
  };

  const handleDeleteRole = (roleId) => {
    setRoleFields(prev => prev.filter(role => role.id !== roleId));

    // Clean up role options for deleted role
    setRoleOptions(prev => {
      const newOptions = { ...prev };
      delete newOptions[roleId];
      return newOptions;
    });

    // Update the actual form data for approver_levels with role IDs
    const updatedRoles = roleFields.filter(role => role.id !== roleId);
    const selectedRoleIds = updatedRoles
      .filter(role => role.selectedRoleId)
      .map(role => role.selectedRoleId);

    setClientData(prev => ({ ...prev, approver_levels: selectedRoleIds, category: prev.categories?.id || prev.category }));
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      toast.error("Invalid file type. Only JPG, JPEG, and PNG are allowed.");
      return;
    }

    try {
      const res = await uploadProfilePhoto(id, file);
      toast.success("Photo updated successfully");
      setClientData(prev => ({ ...prev, photo: res?.data?.photo || URL.createObjectURL(file) }));
    } catch {
      toast.error("Failed to upload photo");
    }
  };

  const handleApproval = async (isApprove) => {
    try {
      setSaving(prev => ({
        ...prev,
        approveAction: isApprove,
        rejectAction: !isApprove
      }));

      const response = await approveClient(id, isApprove);

      if (response?.type === "success") {
        toast.success(response.message || "Successfully Updated");
        if (!isApprove) {
          navigate("/client-management");
          return;
        }
        await fetchClientData();
      } else {
        toast.error(response.message || "Something went wrong");
      }
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      toast.error(errorMessage);
    } finally {
      setSaving(prev => ({
        ...prev,
        approveAction: false,
        rejectAction: false
      }));
    }
  };

  // Updated handleInputChange with validation
  const handleInputChange = (fieldKey, value) => {
    if (fieldKey === 'category') {
      // Find the selected category object and update both the ID and the full object
      const selectedCategory = categoryTypes.find(cat => cat.id === parseInt(value));
      setClientData(prev => ({
        ...prev,
        category: parseInt(value), // Store the ID for API
        categories: selectedCategory || null // Keep the full object for display
      }));
    } else {
      setClientData(prev => ({
        ...prev,
        [fieldKey]: value,
        // Ensure category field is preserved and consistent
        category: prev.categories?.id || prev.category
      }));
    }

    // Rest of your existing validation code...
    setTouched(prev => ({ ...prev, [fieldKey]: true }));

    if (UPDATE_CLIENT_FIELDS[fieldKey]) {
      const error = validateField(fieldKey, value, UPDATE_CLIENT_FIELDS[fieldKey]);
      setValidationErrors(prev => ({
        ...prev,
        [fieldKey]: error
      }));
    }
  };

  // Handle blur event for validation
  const handleBlur = (fieldKey) => {
    // Mark field as touched on blur
    setTouched(prev => ({ ...prev, [fieldKey]: true }));

    // Validate field on blur
    if (UPDATE_CLIENT_FIELDS[fieldKey]) {
      const error = validateField(fieldKey, clientData[fieldKey], UPDATE_CLIENT_FIELDS[fieldKey]);
      setValidationErrors(prev => ({
        ...prev,
        [fieldKey]: error
      }));
    }
  };

  // Handle form submission
  const handleFormSubmit = async (e) => {
    e.preventDefault();

    // Validate all fields before submission
    const errors = {};
    Object.keys(UPDATE_CLIENT_FIELDS).forEach(fieldKey => {
      if (fieldKey === 'approver_levels') {
        // Special validation for approver levels
        const selectedRoleIds = roleFields.filter(role => role.selectedRoleId).map(role => role.selectedRoleId);
        if (UPDATE_CLIENT_FIELDS[fieldKey].required && selectedRoleIds.length === 0) {
          errors[fieldKey] = UPDATE_CLIENT_FIELDS[fieldKey].validationErrorMessage;
        }
      } else {
        const error = validateField(fieldKey, clientData[fieldKey], UPDATE_CLIENT_FIELDS[fieldKey]);
        if (error) {
          errors[fieldKey] = error;
        }
      }
    });

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      // Mark all fields as touched to show errors
      const allTouched = {};
      Object.keys(UPDATE_CLIENT_FIELDS).forEach(fieldKey => {
        allTouched[fieldKey] = true;
      });
      setTouched(allTouched);
      // toast.error('Please fix validation errors before saving');
      return;
    }

    setIsSubmitting(true);
    try {
      // Prepare data for API call
      const updateData = {};
      Object.keys(UPDATE_CLIENT_FIELDS).forEach(fieldKey => {
        if (fieldKey === 'approver_levels') {
          // Handle approver_levels separately
          const selectedRoleIds = roleFields.filter(role => role.selectedRoleId).map(role => role.selectedRoleId);
          updateData[fieldKey] = selectedRoleIds;
        } else if (clientData[fieldKey] !== undefined) {
          if (UPDATE_CLIENT_FIELDS[fieldKey].type === 'checkbox') {
            updateData[fieldKey] = clientData[fieldKey] ? 1 : 0;
          } else {
            updateData[fieldKey] = clientData[fieldKey];
          }
        }
      });

      const response = await updateClient(id, updateData);

      if (response.type === 'success') {
        const responseData = response?.data;
        if (responseData.categories && responseData.categories.id) {
          responseData.category = responseData.categories.id;
        }
        setClientData(response?.data);
        setEditMode(prev => ({ ...prev, general: false }));
        const updatedApproverLevels = roleFields.filter(role => role.selectedRoleId).map(role => role.selectedRoleId);
        setOriginalClientData({
          ...clientData,
          approver_levels: updatedApproverLevels.map(id => ({ id, name: roles.find(r => r.id === id)?.name || '' }))
        });
        setOriginalRoleFields([...roleFields]);
        toast.success('Client updated successfully');
        // Reset validation state
        setValidationErrors({});
        setTouched({});
      } else {
        const errorMessage = extractErrorMessage(response);
        toast.error(errorMessage);
      }
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Enhanced edit mode toggle
  const toggleEditMode = async () => {
    const newEditMode = !editMode.general;

    if (newEditMode) {
      // Entering edit mode - store current state as original
      setOriginalRoleFields([...roleFields]);

      // Load roles for existing data
      await loadRolesForExistingData();
    }

    setEditMode(prev => ({ ...prev, general: newEditMode }));
  };

  // Enhanced handle cancel
  const handleCancel = () => {
    setEditMode(prev => ({ ...prev, general: false }));
    setValidationErrors({});
    setTouched({});

    // Reset form data to original values
    setClientData(originalClientData);

    // Reset role fields to original state
    setRoleFields([...originalRoleFields]);

    // Clear role options
    setRoleOptions({});
  };

  const fetchKycList = async () => {
    try {
      const response = await listDocument(id);
      setKycList(response.data.lists || []);
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      toast.error(errorMessage);
    }
  };

  const fetchDocumentTypes = async () => {
    setLoadingDocumentTypes(true);
    try {
      const response = await listKYCTypes();
      setDocumentTypes(response.data);
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      toast.error(errorMessage);
    } finally {
      setLoadingDocumentTypes(false);
    }
  };

  const handleOpenCreateModal = (fieldType) => {
    setCurrentModalType(fieldType);
    setShowCreateModal(true);
  };

  const handleCreateModalClose = () => {
    setShowCreateModal(false);
    setCurrentModalType(null);
  };

  const handleCreateItem = async (formData) => {
    if (!currentModalType) return;

    setCreatingItem(true);
    try {
      const config = currentModalType === "Document Type"
        ? kycDropDownCreate
        : UPDATE_CLIENT_FIELDS[currentModalType];
      const response = await instance.post(config.apiEndpoint, formData);
      if (response?.data?.type === "success") {
        toast.success(response.data?.message, 'Created');
        if (currentModalType === "Document Type") {
          const newDocumentType = response.data.data;
          setDocumentTypes(prev => {
            const isAlreadyPresent = prev.some(doc => doc.id === newDocumentType.id);
            if (!isAlreadyPresent) {
              return [...prev, newDocumentType];
            }
            return prev;
          });
          setKycFormData(prev => ({
            ...prev,
            document_type: newDocumentType.id
          }));
        } else {
          if (currentModalType === 'category' && response.data.data) {
            const newCategory = response.data.data;
            setCategoryTypes(prev => {
              const isAlreadyPresent = prev.some(category => category.id === newCategory.id);
              if (!isAlreadyPresent) {
                return [...prev, newCategory];
              }
              return prev;
            });
            setClientData(prev => ({
              ...prev,
              category: newCategory.id,
              categories: newCategory
            }));
            setValidationErrors(prev => {
              const { category, ...rest } = prev;
              return rest;
            });
          }
        }
        // Show success message
        handleCreateModalClose();
      }
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      toast.error(errorMessage);
    } finally {
      setCreatingItem(false);
    }
  };
  const handleTabChange = async (key) => {
    setActiveTab(key);
    if (key === 'clientDoc') {
      await fetchKycList();
      await fetchDocumentTypes();
    }
  };
  const handleKycInputChange = (key, value) => {
    setKycFormData(prev => {
      const updatedData = {
        ...prev,
        [key]: key === 'document_type' ? parseInt(value) || '' :
          key === 'document_number' ? value || '' : value
      };
      return updatedData;
    });

    // Clear validation error for this field when user makes a selection
    if (value && kycErrors[key]) {
      setKycErrors(prev => {
        const updatedErrors = { ...prev };
        delete updatedErrors[key];
        return updatedErrors;
      });
    }

    // Validation logic for expires_date and notify_day_before
    setKycFormData(prev => {
      const currentData = {
        ...prev,
        [key]: key === 'document_type' ? parseInt(value) || '' : value
      };

      // Get current date (without time for comparison)
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Validate expires_date
      if (key === 'expires_date' || (key === 'notify_day_before' && currentData.expires_date)) {
        const expiresDate = currentData.expires_date;
        const notifyDaysBefore = currentData.notify_day_before;

        setKycErrors(prev => {
          const updatedErrors = { ...prev };

          // Clear previous errors for these fields
          delete updatedErrors.expires_date;
          delete updatedErrors.notify_day_before;

          if (expiresDate) {
            const expiryDateObj = new Date(expiresDate);
            expiryDateObj.setHours(0, 0, 0, 0);

            // Check if expires_date is not the current date
            if (expiryDateObj.getTime() === today.getTime()) {
              updatedErrors.expires_date = "Expiry date cannot be today's date";
            }
            // Check if expires_date is in the past
            else if (expiryDateObj < today) {
              updatedErrors.expires_date = "Expiry date cannot be in the past";
            }
            // If expires_date is valid and not null, notify_day_before should be required
            else if (!notifyDaysBefore || notifyDaysBefore.trim() === '') {
              updatedErrors.notify_day_before = "Notify before days is required";
            }
            // Validate notify_day_before value and gap alignment
            else {
              const notifyDays = parseInt(notifyDaysBefore);

              // Check if notify_day_before is a valid positive number
              if (isNaN(notifyDays) || notifyDays <= 0) {
                updatedErrors.notify_day_before = "Notify before days must be a positive number";
              } else {
                // Calculate the gap between today and expires_date
                const timeDifference = expiryDateObj.getTime() - today.getTime();
                const daysDifference = Math.ceil(timeDifference / (1000 * 60 * 60 * 24));

                // Check if notify_day_before is greater than the available days
                if (notifyDays > daysDifference) {
                  updatedErrors.notify_day_before = `Notify before days should be less than the days until expiry (${daysDifference})`;
                }
              }
            }
          }

          return updatedErrors;
        });
      }

      return currentData;
    });
  };

  const handleKycFileChange = (e) => {
    const file = e.target.files[0];
    setKycFormData(prev => ({
      ...prev,
      photo: file
    }));

    // Clear validation error for photo field when user selects a file
    if (file && kycErrors.photo) {
      setKycErrors(prev => {
        const updatedErrors = { ...prev };
        delete updatedErrors.photo;
        return updatedErrors;
      });
    }
  };

  const handleKycSave = async () => {
    setKycErrors({});
    try {
      // Validate required fields
      const errors = {};

      if (!kycFormData.document_number) {
        errors.document_number = 'Document Number is required';
      }
      if (!kycFormData.document_type) {
        errors.document_type = 'Document type is required';
      }
      if (!editingKycId && !kycFormData.photo) {
        errors.photo = 'Document file is required';
      }
      if (!editingKycId && kycFormData.expires_date && (kycFormData.notify_day_before === null || kycFormData.notify_day_before === undefined || kycFormData.notify_day_before === '')) {
        errors.notify_day_before = 'Notify date field is required';
      }

      if (Object.keys(errors).length > 0) {
        setKycErrors(errors);
        // toast.error('Please fill all required fields');
        return;
      }
      setSaving(prev => ({ ...prev, employeeIdentity: true }));
      const res = await insertDocument(id, kycFormData);
      if (res.status === "success") {
        toast.success('Document added successfully');
      } else {
        toast.success(res.message, 'Something went wrong');
      }
      // Reset form and refresh list
      setKycFormData({
        document_type: null,
        photo: null,
        expires_date: null,
        notify_day_before: null,
        document_number: null

      });
      setShowKycForm(false);
      setEditingKycId(null);
      await fetchKycList();

    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      toast.error(errorMessage);
    } finally {
      setSaving(prev => ({ ...prev, employeeIdentity: false }));
    }
  };

  const handleKycDelete = (kycId) => {
    setConfirmModal({
      show: true,
      message: "Are you sure you want to delete this document?",
      onConfirm: async () => {
        try {
          await deleteDocument(id, kycId);
          toast.success('Document deleted successfully');
          await fetchKycList();
        } catch (error) {
          const errorMessage = extractErrorMessage(error);
          toast.error(errorMessage);
        }
      }
    });
  };

  const handleKycCancel = () => {
    setKycErrors({})
    setKycFormData({
      document_number: null,
      document_type: null,
      photo: null,
      expires_date: null,
      notify_day_before: null
    });
    setShowKycForm(false);
    setEditingKycId(null);
  };
  const getDocumentTypeLabel = (documentTypeObj) => {
    return documentTypeObj?.name || '-';
  };
  const renderEmployeeIdentitySection = () => (
    <div className="mb-5">
      {/* KYC Form */}
      {showKycForm && (
        <Card className="mb-3">
          <Card.Body>
            <h6 className="mb-3 text-primary">{'Add New Document'}</h6>
            <Row className="align-items-end">
              <Col xs={12} sm={4} className="mb-3">
                <Form.Label className="fw-medium">
                  Document Number <span className="text-danger">*</span>
                </Form.Label>
                <div className="d-flex">
                  <Form.Control
                    type="text"
                    placeholder='Enter Document Number'
                    value={kycFormData.document_number || ''}
                    required
                    onChange={(e) => handleKycInputChange('document_number', e.target.value)}
                    isInvalid={!!kycErrors.document_number}
                  />
                </div>
                <div style={{ height: '20px', fontSize: '0.875rem' }} className="text-danger mb-1">
                  {kycErrors.document_number}
                </div>
              </Col>
              <Col xs={12} sm={4} className="mb-3">
                <Form.Label className="fw-medium">
                  Document Type <span className="text-danger">*</span>
                </Form.Label>
                <div className="d-flex">
                  <Form.Select
                    value={kycFormData.document_type}
                    isInvalid={!!kycErrors.document_type}
                    onChange={(e) => handleKycInputChange('document_type', e.target.value)}
                  >
                    <option value="">
                      {loadingDocumentTypes ? 'Loading...' : 'Select Document Type...'}
                    </option>
                    {Array.isArray(documentTypes) && documentTypes.length > 0 ? (
                      documentTypes.map(type => (
                        <option key={type.id} value={type.id}>
                          {truncateTextOnly(type.name, 50)}
                        </option>
                      ))
                    ) : !loadingDocumentTypes ? (
                      <option value="" disabled>
                        No Document Types Found
                      </option>
                    ) : null}
                  </Form.Select>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className='no-hover ml-2'
                    onClick={() => handleOpenCreateModal('Document Type')}
                  >
                    <FaPlus />
                  </Button>
                </div>

                <div style={{ height: '20px', fontSize: '0.875rem' }} className="text-danger">
                  {kycErrors.document_type}
                </div>
              </Col>

              <Col xs={12} sm={6} lg={4} className="mb-3">
                <Form.Label className="fw-medium">
                  Upload File <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*,.pdf"
                  onChange={handleKycFileChange}
                  isInvalid={!!kycErrors.photo}
                />
                <div style={{ height: '20px', fontSize: '0.875rem' }} className="text-danger">
                  {kycErrors.photo}
                </div>
                {editingKycId && (
                  <Form.Text className="text-muted">
                    Leave empty to keep existing file
                  </Form.Text>
                )}
              </Col>

              <Col xs={12} sm={6} lg={4} className="mb-3">
                <Form.Label className="fw-medium">
                  Expiry Date{' '}
                  {(!clientData?.poc_mail || clientData.poc_mail.trim() === '') && (
                    <span className="text-warning" style={{ height: '20px', fontSize: '0.875rem' }}>
                      (POC mail is not added)
                    </span>
                  )}
                </Form.Label>
                <Form.Control
                  type="date"
                  onChange={(e) => handleKycInputChange('expires_date', e.target.value)}
                  isInvalid={!!kycErrors.expires_date}
                />
                <div style={{ height: '20px', fontSize: '0.875rem' }} className="text-danger">
                  {kycErrors.expires_date}
                </div>
              </Col>
              {kycFormData.expires_date && (
                <Col xs={12} sm={6} lg={4} className="mb-3">
                  <Form.Label className="fw-medium">
                    Notify Before <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    required
                    onChange={(e) => handleKycInputChange('notify_day_before', e.target.value)}
                    isInvalid={!!kycErrors.notify_day_before}
                  />
                  <div style={{ height: '20px', fontSize: '0.875rem' }} className="text-danger mb-1">
                    {kycErrors.notify_day_before}
                  </div>
                </Col>
              )}
              <Col xs={12} className="mb-2 d-flex gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  className='no-hover fw-bold'
                  onClick={handleKycSave}
                  disabled={saving.employeeIdentity || Object.keys(kycErrors).length > 0}
                  style={{ width: '100px', fontWeight: 'bold' }}
                >
                  {saving.employeeIdentity ? 'Saving...' : (editingKycId ? 'Update' : 'Save')}
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className='no-hover fw-bold'
                  onClick={handleKycCancel}
                  disabled={saving.employeeIdentity}
                  style={{ width: '100px', fontWeight: 'bold' }}
                >
                  Cancel
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* KYC List */}
      {kycList.length > 0 && (
        kycList.map((kyc, index) => (
          <Card key={kyc.id} className="mb-3">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start mb-2">
                <h6 className="mb-0 text-primary">Document: {index + 1}</h6>
                <div className="d-flex gap-2">
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleKycDelete(kyc.id)}
                    className="d-flex align-items-center no-hover"
                  >
                    <FaTrash className="me-1" />
                    <span>Delete</span>
                  </Button>
                </div>
              </div>
              <Row>
                <Col sm={4} className="mb-2">
                  <p className="fw-medium mb-1">Document Number</p>
                  <p className="text-muted border p-2 rounded mb-0">
                    {kyc?.document_number || "N/A"}
                  </p>
                </Col>
                <Col sm={4} className="mb-2">
                  <p className="fw-medium mb-1">Document Type</p>
                  <p className="text-muted border p-2 rounded mb-0">
                    {getDocumentTypeLabel(kyc.document_types)}
                  </p>
                </Col>
                <Col sm={4} className="mb-2">
                  <div>
                    <p className="fw-medium mb-1">File</p>
                    <div className="p-2 rounded border">
                      {kyc.file ? (
                        <a
                          href={kyc.file}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary text-decoration-none d-inline-flex align-items-center"
                        >
                          <FaEye className="me-1" />
                          <span>View File</span>
                        </a>
                      ) : (
                        <span className="text-muted">No file uploaded</span>
                      )}
                    </div>
                  </div>
                </Col>
                <Col sm={4} className="mb-2">
                  <span className="fw-medium me-2">Expiry Date</span>
                  {(!clientData?.poc_mail || clientData.poc_mail.trim() === '') && (
                    <span className="text-warning" style={{ fontSize: '0.875rem' }}>
                      (POC mail is not added)
                    </span>
                  )}
                  <p className="text-muted border p-2 rounded mb-0">
                    {kyc?.expires_date || "No date"}
                  </p>
                </Col>
                <Col sm={4} className="mb-2">
                  <p className="fw-medium mb-1">Notify Before</p>
                  <p className="text-muted border p-2 rounded mb-0">
                    {kyc?.notify_day_before}
                  </p>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        ))
      )}

      {/* Add Button if no records */}
      {!showKycForm && (
        <div className="text-center py-4">
          <Button
            variant="outline-primary"
            onClick={() => setShowKycForm(true)}
            className="d-inline-flex align-items-center no-hover"
          >
            <FaPlus className="me-2" /> Add Document
          </Button>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <Base>
        <Container>
          <div className="d-flex justify-content-center align-items-center" style={{ height: '60vh' }}>
            <div className="text-center">
              <Spinner animation="border" role="status" className="mb-3">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
              <p className="text-muted">Loading client data...</p>
            </div>
          </div>
        </Container>
      </Base>
    );
  }

  return (
    <Base>
      <Container>
        <Row>
          <Col className="mr-auto text-center">
            <Breadcrumb>
              <Breadcrumb.Item linkAs={NavLink} linkProps={{ to: '/' }}>Dashboard</Breadcrumb.Item>
              <Breadcrumb.Item linkAs={NavLink} linkProps={{ to: '/client-management' }}>Client management</Breadcrumb.Item>
              <Breadcrumb.Item active>Client Process</Breadcrumb.Item>
            </Breadcrumb>
          </Col>
        </Row>
        <Row>
          <Col>
            <Card className="shadow-sm border-0">
              <Card.Header className="bg-white border-bottom p-1">
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center">
                    <div className="position-relative me-3">
                      {clientData.photo ? (
                        <Image
                          src={clientData.photo}
                          alt="Client profile"
                          width={64}
                          height={64}
                          roundedCircle
                          className="border border-3 border-blue p-1 object-fit-cover"
                          style={{ width: '64px', height: '64px', objectFit: 'cover' }}
                        />
                      ) : (
                        <FaUserCircle
                          size={64}
                          className="text-secondary border border-3 border-blue rounded-circle p-1"
                        />
                      )}
                    </div>
                    <div>
                      <h4 className="mb-1 fw-semibold">{truncateText(clientData.client_name, 40) || 'Client Name'}</h4>
                      <div className="d-flex align-items-center">
                        <span className="text-muted small">{truncateText(clientData.email, 40) || 'No email'}</span>
                        <div className="form-check form-switch ms-2">
                          <input
                            className="form-check-input cursor-pointer"
                            type="checkbox"
                            id={`activeToggle-${id || 'default'}`}
                            checked={clientData.is_active || false}
                            disabled={isUpdating}
                            onChange={(e) => {
                              handleStatusToggle(e.target.checked);
                            }}
                          />
                          <Badge
                            bg={clientData.is_active ? 'success' : 'secondary'}
                            as="label"
                            htmlFor={`activeToggle-${id || 'default'}`}
                            className="form-check-label"
                            style={{
                              cursor: isUpdating ? 'not-allowed' : 'pointer',
                              opacity: isUpdating ? 0.6 : 1
                            }}
                          >
                            {isUpdating ? 'Updating...' : (clientData.is_active ? 'Active' : 'Inactive')}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className='d-flex align-item-center flex-wrap gap-2'>
                    <input
                      type="file"
                      accept="image/*"
                      ref={fileInputRef}
                      className="d-none"
                      onChange={handlePhotoChange}
                    />
                    <Button
                      className="d-flex align-items-center justify-content-between fw-bold"
                      variant="primary"
                      size="sm"
                      onClick={() => fileInputRef.current.click()}
                    >
                      <span className="me-auto pe-1"><IoCameraOutline /></span>
                      <span className="mx-auto">Update Photo</span>
                    </Button>
                  </div>
                </div>
                {(clientData?.approved === 0) && (
                  <div className="p-3 mt-3 border-top">
                    <div className="d-flex justify-content-between align-items-center flex-wrap">
                      <div>
                        <h6 className="text-primary fw-semibold mb-3 d-flex align-items-center">
                          <MdDoubleArrow className="me-1" />
                          Permanent Approval
                        </h6>
                        <div className="d-flex align-items-center overflow-auto pb-2" style={{ gap: '1rem' }}>
                          {Object.values(clientData.progress || {})
                            .sort((a, b) => b.level - a.level)
                            .map((step, index, array) => (
                              <React.Fragment key={step.level}>
                                <div className="d-flex align-items-center" style={{ minWidth: 'max-content', gap: '0.5rem' }}>
                                  <div className="d-flex flex-column align-items-center">
                                    <div
                                      className="rounded-circle d-flex align-items-center justify-content-center"
                                      style={{
                                        width: '32px',
                                        height: '32px',
                                        backgroundColor: step.progress === 1 ? '#28a745' : '#dee2e6',
                                        color: step.progress === 1 ? '#fff' : '#6c757d',
                                      }}
                                    >
                                      {step.progress === 1 ? <FaCheck size={14} /> : <FaUser size={14} />}
                                    </div>
                                    <p className="text-muted mt-1 mb-0 text-center" style={{ fontSize: '0.75rem' }}>{step.name}</p>
                                  </div>
                                  {index < array.length - 1 && (
                                    <div style={{ width: '48px', height: '2px', backgroundColor: step.progress === 1 ? '#28a745' : '#dee2e6' }}></div>
                                  )}
                                </div>
                              </React.Fragment>
                            ))}
                        </div>
                      </div>
                      <div className="d-flex gap-2 mt-2 mt-md-0">
                        <Button variant="outline-primary" size="sm" className="fw-bold no-hover" style={{ width: '100px' }} disabled={saving.approveAction || saving.rejectAction} onClick={() => handleApproval(true)}>{saving.approveAction ? 'Approving...' : 'Approve'}</Button>
                        <Button variant="outline-danger" size="sm" className="fw-bold no-hover" style={{ width: '100px' }} disabled={saving.approveAction || saving.rejectAction} onClick={() => handleApproval(false)}>{saving.rejectAction ? 'Rejecting...' : 'Reject'}</Button>
                      </div>
                    </div>
                  </div>
                )}
              </Card.Header>
              <Card.Body className="d-flex flex-column p-0" style={{ height: '70vh' }}>
                <div className="bg-white border-bottom">
                  <Tabs
                    activeKey={activeTab}
                    onSelect={handleTabChange}
                    className="border-bottom-0 custom-tabs px-3 pt-3"
                    variant="underline"
                  >
                    <Tab eventKey="general" title="General" />
                    <Tab eventKey="clientDoc" title="Client Documents" />
                    <Tab eventKey="clientCredentials" title="Client Credentials" />
                    <Tab eventKey="monthlyRates" title="Monthly Rates" />
                  </Tabs>
                </div>
                <div className="flex-grow-1 overflow-auto">
                  <div className="tab-content">
                    {activeTab === 'general' && (
                      <div className="p-3">
                        <Row className="gy-3">

                          <div className="d-flex justify-content-between align-items-center sticky-top bg-white py-2 px-3 mb-3" style={{ zIndex: 10, marginLeft: '-12px', marginRight: '-12px', marginTop: '-12px' }}>
                            {/* Left section: Icon + Title */}
                            <div className="d-flex align-items-center">
                              <MdDoubleArrow className=" text-primary rounded-circle p-1" size={24} />
                              <h6 className="fw-semibold text-primary mb-0">Client Information</h6>
                            </div>

                            {/* Right section: Edit or Save/Cancel buttons */}
                            {editMode.general ? (
                              <div className="d-flex align-items-center gap-2">
                                <Button
                                  type="button"
                                  onClick={handleFormSubmit}
                                  variant="primary"
                                  size="sm"
                                  className="fw-bold"
                                  disabled={isSubmitting}
                                  style={{ width: '100px' }}
                                >
                                  {isSubmitting ? 'Saving...' : 'Save'}
                                </Button>
                                <Button
                                  type="button"
                                  variant="secondary"
                                  size="sm"
                                  className="fw-bold"
                                  style={{ width: '100px' }}
                                  onClick={handleCancel}
                                  disabled={isSubmitting}
                                >
                                  Cancel
                                </Button>
                              </div>
                            ) : (
                              <div className="d-flex align-items-center gap-2">
                                <Button
                                  type="button" // <- important!
                                  className="d-flex align-items-center"
                                  variant="link"
                                  size="sm"
                                  onClick={toggleEditMode}
                                >
                                  <FaEdit className="me-1" />
                                  Edit
                                </Button>
                              </div>
                            )}
                          </div>

                          {Object.keys(UPDATE_CLIENT_FIELDS).map((fieldKey) => {
                            const field = UPDATE_CLIENT_FIELDS[fieldKey];
                            const hasError = !!(touched[fieldKey] && validationErrors[fieldKey]);

                            // Handle checkbox_group type (approver_levels) with hierarchical selection
                            if (field.type === 'checkbox_group') {
                              return (
                                <Col xs={12} key={fieldKey} className="mb-6">
                                  <div className="checkbox-group">
                                    <Form.Label className="mb-4 pb-2 d-block fw-bold border-bottom border-secondary" style={{ fontSize: '20px', lineHeight: '1.75', color: '#0f172a' }}>
                                      {field.label}
                                      {field.required && <span className="text-danger ms-1">*</span>}
                                    </Form.Label>
                                    <Row>
                                      <Col className="mb-6">
                                        <div className="d-flex align-items-center gap-3 flex-wrap">
                                          {roleFields.map((role, index) => (
                                            <div key={role.id} className="d-flex align-items-center gap-2">
                                              {index > 0 && <span style={{ color: '#0061fe', fontWeight: '600', fontSize: '24px' }}>→</span>}

                                              <div className="d-flex flex-column">
                                                {/* Show text label if it's not the first role and has empty options */}
                                                {index > 0 &&
                                                  !loadingRoles[role.id] &&
                                                  roleOptions[role.id] &&
                                                  roleOptions[role.id].length === 0 ? (
                                                  <div
                                                    className="d-flex align-items-center justify-content-center bg-light border rounded text-muted"
                                                    style={{ width: '160px', height: '31px', fontSize: '12px' }}
                                                  >
                                                    Top hierarchy reached
                                                  </div>
                                                ) : (
                                                  <Form.Select
                                                    className={`shadow-sm ${role.error ? 'border-danger' : ''}`}
                                                    style={{ width: '160px', fontSize: '14px' }}
                                                    value={role.value}
                                                    onChange={(e) => handleRoleChange(e, role.id)}
                                                    isInvalid={role.error}
                                                    disabled={isSubmitting || !editMode.general || loadingRoles[role.id]}
                                                    size="sm"
                                                  >
                                                    <option value="">
                                                      {loadingRoles[role.id] ? 'Loading...' : 'Select Role'}
                                                    </option>
                                                    {(roleOptions[role.id] || []).map((roleOption) => (
                                                      <option key={roleOption.id} value={roleOption.name}>
                                                        {roleOption.name}
                                                      </option>
                                                    ))}
                                                    {/* Show the current value as an option even if not in edit mode */}
                                                    {!editMode.general && role.value && !(roleOptions[role.id] || []).some(option => option.name === role.value) && (
                                                      <option key="current-value" value={role.value}>{role.value}</option>
                                                    )}
                                                  </Form.Select>
                                                )}

                                                {role.error && (
                                                  <div className="text-danger" style={{ fontSize: '12px', marginTop: '4px' }}>
                                                    Please select a role.
                                                  </div>
                                                )}
                                              </div>

                                              {editMode.general && index === roleFields.length - 1 && (
                                                <div className="d-flex align-items-center gap-1">
                                                  {/* Only show add button if not at top hierarchy */}
                                                  {!(index > 0 &&
                                                    !loadingRoles[role.id] &&
                                                    roleOptions[role.id] &&
                                                    roleOptions[role.id].length === 0) && (
                                                      <Button
                                                        variant="link"
                                                        size="sm"
                                                        className="p-0 fw-bold !no-underline"
                                                        style={{ color: '#0061fe', fontSize: '18px' }}
                                                        onClick={() => handleAddRole(role.id)}
                                                        disabled={isSubmitting}
                                                        title="Add Role"
                                                      >
                                                        <FaPlus className="text-red-500 hover:text-red-700" />
                                                      </Button>
                                                    )}

                                                  {roleFields.length > 1 && (
                                                    <Button
                                                      variant="link"
                                                      size="sm"
                                                      className="p-0 fw-bold !no-underline"
                                                      style={{ color: '#ef4444' }}
                                                      onClick={() => handleDeleteRole(role.id)}
                                                      disabled={isSubmitting}
                                                      title="Remove Role"
                                                      onMouseEnter={(e) => e.target.style.color = '#dc2626'}
                                                      onMouseLeave={(e) => e.target.style.color = '#ef4444'}
                                                    >
                                                      <FaTrash className="text-red-500 hover:text-red-700" />
                                                    </Button>
                                                  )}
                                                </div>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      </Col>
                                    </Row>
                                    {hasError && (
                                      <div className="text-danger mt-2" style={{ fontSize: '14px' }}>
                                        {validationErrors[fieldKey]}
                                      </div>
                                    )}
                                  </div>
                                </Col>
                              );
                            }

                            return (
                              <Col md={6} key={fieldKey}>
                                <Form.Group>
                                  <Form.Label className="fw-bold">
                                    {field.label}
                                    {field.required && <span className="text-danger ms-1">*</span>}
                                  </Form.Label>

                                  {/* Text Input Fields */}
                                  {field.type === 'text' && (
                                    <Form.Control
                                      type="text"
                                      name={field.name}
                                      placeholder={field.placeholder}
                                      value={clientData[fieldKey] || ''}
                                      disabled={!editMode.general}
                                      onChange={(e) => handleInputChange(fieldKey, e.target.value)}
                                      onBlur={() => handleBlur(fieldKey)}
                                      isInvalid={hasError}
                                      maxLength={field.maxLength}
                                      required={field.required}
                                    />
                                  )}

                                  {/* Select Fields */}
                                  {field.type === 'select' && (
                                    <>
                                      {editMode.general ? (
                                        <div className="d-flex align-items-center">
                                          <Form.Select
                                            name={field.name}
                                            value={
                                              fieldKey === 'category'
                                                ? clientData.categories?.id || ''
                                                : clientData[fieldKey] || ''
                                            }
                                            disabled={!editMode.general}
                                            onChange={(e) => handleInputChange(fieldKey, e.target.value)}
                                            onBlur={() => handleBlur(fieldKey)}
                                            isInvalid={hasError}
                                            required={field.required}
                                          >
                                            <option value="">Select {field.label}</option>
                                            {fieldKey === 'category' ? (
                                              Array.isArray(categoryTypes) && categoryTypes.length > 0 ? (
                                                categoryTypes.map(type => (
                                                  <option key={type.id} value={type.id}>
                                                    {truncateTextOnly(type.name, 50)}
                                                  </option>
                                                ))
                                              ) : !loadingCategoryTypes ? (
                                                <option value="" disabled>
                                                  No categories Found
                                                </option>
                                              ) : null

                                            ) : fieldKey === 'client_class' ? (
                                              // NEW: Static options for client_class
                                              field.options.map(option => (
                                                <option key={option.value} value={option.value}>
                                                  {option.label}
                                                </option>
                                              ))
                                            ) : (
                                              userData?.map((user) => (
                                                <option key={user.user_id} value={user.user_id}>
                                                  {truncateText(user.name, 30)}
                                                </option>
                                              ))
                                            )}
                                          </Form.Select>
                                          {fieldKey === 'category' && (
                                            <Button
                                              variant="outline-primary"
                                              size="sm"
                                              className='no-hover ml-2'
                                              onClick={() => handleOpenCreateModal('category')}
                                            >
                                              <FaPlus />
                                            </Button>
                                          )}
                                        </div>
                                      ) : (
                                        <Form.Control
                                          type="text"
                                          value={
                                            fieldKey === 'category'
                                              ? clientData.categories?.name || 'Not assigned'
                                              : fieldKey === 'client_class'
                                                ? clientData[fieldKey] || 'Not assigned'
                                                : userData?.find(user => user.user_id == clientData[fieldKey])?.name || 'Not assigned'
                                          }
                                          disabled
                                          readOnly
                                        />
                                      )}
                                    </>
                                  )}
                                  {field.type === 'checkbox' && (
                                    <Form.Check
                                      type="checkbox"
                                      name={field.name}
                                      id={`${fieldKey}-customCheck`}
                                      label={field.label}
                                      className='px-4'
                                      checked={!!clientData[fieldKey]}
                                      onChange={(e) => handleInputChange(fieldKey, e.target.checked)}
                                      disabled={!editMode.general || isSubmitting}
                                    />

                                  )}

                                  {/* Show validation error if field is touched and has error */}
                                  {touched[fieldKey] && validationErrors[fieldKey] && (
                                    <Form.Control.Feedback type="invalid" className="d-block">
                                      {validationErrors[fieldKey]}
                                    </Form.Control.Feedback>
                                  )}
                                </Form.Group>
                              </Col>
                            );
                          })}
                        </Row>
                      </div>
                    )}
                    {activeTab === 'clientDoc' && (
                      <div className="p-3">
                        {renderEmployeeIdentitySection()}
                      </div>
                    )}
                    {activeTab === 'clientCredentials' && (
                      <div className="p-3">
                        <ClientCredentials clientId={id} />
                      </div>
                    )}
                    {activeTab === 'monthlyRates' && (
                      <div className="p-3">
                        <MonthlyRates clientId={id} />
                      </div>
                    )}
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      <DynamicModal
        show={confirmModal.show}
        onHide={() => setConfirmModal({ ...confirmModal, show: false })}
        title="Confirm Delete"
        buttons={[
          { label: 'Cancel', variant: 'secondary', action: 'custom', onClick: () => setConfirmModal({ ...confirmModal, show: false }) },
          { label: 'Delete', variant: 'danger', action: 'custom', onClick: async () => { setConfirmModal({ ...confirmModal, show: false }); if (confirmModal.onConfirm) await confirmModal.onConfirm(); } }
        ]}
      >
        <p>{confirmModal.message}</p>
      </DynamicModal>
      <DynamicCreateModal
        show={showCreateModal}
        onHide={handleCreateModalClose}
        config={currentModalType === "Document Type" ? kycDropDownCreate : UPDATE_CLIENT_FIELDS[currentModalType]}
        onSubmit={handleCreateItem}
        isSubmitting={creatingItem}
      />
    </Base>
  );
}

export default ClientProcess;